import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Users, IndianRupee, TrendingUp, ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ASSIGNABLE_ROLES } from '../lib/roles';

const UserRevenueManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({
    total_users: 0,
    total_revenue: 0,
    total_orders: 0,
    avg_order_value: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const totalRevenue = statsRes.data.total_revenue || 0;
      const totalOrders = ordersRes.data.length;
      const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
      
      setStats({
        total_users: statsRes.data.total_users,
        total_revenue: totalRevenue,
        total_orders: totalOrders,
        avg_order_value: avgOrderValue
      });
      setUsers(usersRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.patch(
        `${API}/admin/users/${userId}`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User role updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const handleToggleUserApproval = async (userId, currentStatus) => {
    try {
      await axios.patch(
        `${API}/admin/users/${userId}`,
        { is_approved: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order? This action will be logged.')) return;
    
    try {
      await axios.delete(`${API}/admin/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order deleted and logged successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  return (
    <div className="min-h-screen" style={{background: 'var(--background)'}}>
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/super-admin')}
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Dashboard</span>
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">User & Revenue Management</h1>

        <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <Users className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Total Users</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total_users}</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <IndianRupee className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Total Revenue</p>
            <p className="text-3xl font-bold text-gray-900">₹{Math.round(stats.total_revenue || 0)}</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <TrendingUp className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Total Orders</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total_orders}</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{backgroundColor: '#E8E4C9'}}>
                <IndianRupee className="h-6 w-6" style={{color: '#A89968'}} />
              </div>
            </div>
            <p className="text-gray-600 text-sm">Avg Order Value</p>
            <p className="text-3xl font-bold text-gray-900">₹{Math.round(stats.avg_order_value || 0)}</p>
          </div>
        </div>

        <Tabs defaultValue="users" className="w-full">
          <TabsList className="mb-6" style={{backgroundColor: 'white'}}>
            <TabsTrigger value="users">User Management</TabsTrigger>
            <TabsTrigger value="revenue">Revenue Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email/Phone</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Role</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Join Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">{user.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{user.email || user.phone}</td>
                        <td className="px-6 py-4">
                          <Select value={user.role} onValueChange={(value) => handleUpdateUserRole(user.id, value)}>
                            <SelectTrigger className="w-32">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {ASSIGNABLE_ROLES.map((r) => (
                                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-6 py-4">
                          <Button
                            variant={user.is_approved ? "ghost" : "destructive"}
                            size="sm"
                            onClick={() => handleToggleUserApproval(user.id, user.is_approved)}
                          >
                            {user.is_approved ? 'Approved' : 'Revoked'}
                          </Button>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="revenue">
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="border-b">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Order ID</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Customer</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Items</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Total Amount</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Discount</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Final Amount</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {orders.map((order) => (
                      <tr key={order.id}>
                        <td className="px-6 py-4 text-sm text-gray-900">{order.id.slice(0, 8)}...</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {order.customer_name || users.find(u => u.id === order.user_id)?.name || order.user_id.slice(0, 8) + '...'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">{order.items.length}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">₹{Math.round(order.total_amount)}</td>
                        <td className="px-6 py-4 text-sm text-green-600">-₹{Math.round(order.discount)}</td>
                        <td className="px-6 py-4 text-sm font-bold" style={{color: '#A89968'}}>₹{Math.round(order.final_amount)}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteOrder(order.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Delete order (will be logged)"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default UserRevenueManagement;
